<template>
  <Menu
    v-if="shouldShowSettingsMenu"
    data-testid="topBar-button-settingsMenu"
    data-tourid="topBar-button-settingsMenu"
    :closeOnContentClick="adminToolOptions.length === 0"
    :options="settingsMenuOptions"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        data-testid="topBar-button-gear"
        v-on="on"
        elevation="0"
        fab
        small
        :color="$vuetify.breakpoint.mobile ? 'white' : 'transparent'"
      >
        <v-icon color="blue-grey darken-3">mdi-cog-outline</v-icon>
      </v-btn>
    </template>
  </Menu>
</template>
<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import access from '@/lib/access'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import Menu from '@/components/UI/Menu.vue'

export default {
  name: 'SettingsMenu',
  components: {
    Menu,
  },
  mixins: [mixin, trackerMixin],

  computed: {
    ...mapGetters(['account', 'organization', 'organizationId', 'siteId']),

    shouldShowSettingsMenu() {
      return (
        this.showMigrationToolOption ||
        this.userIsSiteAdmin ||
        this.userIsOrgAdmin ||
        this.userIsAdmin ||
        this.isLeadershipOrCoach
      )
    },

    userIsOrgAdmin() {
      return access.isOrganizationAdmin(this.account)
    },

    userIsAdmin() {
      return access.isAdmin(this.account)
    },

    isSiteAdmin() {
      return access.isSiteAdmin(this.account)
    },

    isLeadershipOrCoach() {
      return access.isLeadershipOrCoach(this.account)
    },

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    canAccessAIOrganizationSettings() {
      return (
        this.aiFeaturesAreEnabled &&
        (access.isOrganizationAIManager(this.account) ||
          access.isAIAdmin(this.account))
      )
    },

    settingOption() {
      //Users with multiple admin permissions should only see the settings option corresponding to the higher permission level.
      //E.g. A user with "BU admin" and "Org admin" permissions should only see the "Organization settings" option and not the "Sites settings" one.
      const currentSiteId = this.$route?.params?.siteId || this.siteId
      if (this.isSiteAdmin && !this.userIsOrgAdmin) {
        return {
          icon: 'mdi-domain',
          id: 'siteSettings',
          url: `/organizations/${this.organizationId}/sites/${currentSiteId}/settings`,
          title: this.$t('menu.site_settings'),
          tourId: 'userMenu-option-siteSettings',
        }
      }

      if (this.userIsOrgAdmin) {
        return {
          icon: 'mdi-cog-outline',
          id: 'organizationSettings',
          url: `/organization-settings/${this.organizationId}`,
          tourId: 'userMenu-option-organizationSettings',
          title: this.$t('menu.organization_settings'),
        }
      }

      return null
    },

    settingsMenuOptions() {
      return _.compact([
        this.settingOption && {
          testId: `topBar-item-${this.settingOption.title}`,
          tourId: this.settingOption.tourId,
          icon: this.settingOption.icon,
          id: `item-${this.settingOption.title}`,
          title: this.settingOption.title,
          action: () => this.handleNavigation(this.settingOption),
        },

        ...this.adminToolOptions,

        this.additionalOptions.length > 0 && { divider: true },
        ...this.additionalOptions,
      ])
    },

    adminToolOptions() {
      return _.compact([
        this.userIsAdmin && {
          testId: 'settingMenu-button-admin',
          icon: 'mdi-account-key-outline',
          id: 'admingroup',
          title: this.$t('menu.admin_tools'),
          subOptions: _.compact([
            this.userIsAdmin && {
              testId: 'settingsMenu-button-organizations',
              id: 'organizations',
              title: this.$t('common.organization_other'),
              url: '/admin/organizations',
              action: () =>
                this.handleNavigation({
                  id: 'organizations',
                  url: '/admin/organizations',
                }),
            },
            this.userIsAdmin && {
              testId: 'settingsMenu-button-activityLogs',
              id: 'activityLogs',
              title: this.$t('menu.admin.activity_logs'),
              url: '/admin/logs',
              action: () =>
                this.handleNavigation({
                  id: 'activityLogs',
                  url: '/admin/logs',
                }),
            },
          ]),
        },
      ])
    },

    additionalOptions() {
      return _.compact([
        this.isLeadershipOrCoach && {
          icon: 'mdi-account-cog-outline',
          id: 'admin-people',
          url: '/people',
          tourId: 'userMenu-option-people',
          title: this.$t('menu.admin.admin_people'),
          action: () =>
            this.handleNavigation({ id: 'admin-people', url: '/people' }),
        },
        this.canAccessAIOrganizationSettings && {
          icon: 'mdi-creation-outline',
          id: 'aiSettings',
          url: '/ai-settings',
          title: this.$t('menu.ai_settings'),
          action: () =>
            this.handleNavigation({
              id: 'aiSettings',
              url: '/ai-settings',
            }),
        },
        this.accessAllowed('audit-logs') &&
          this.userIsOrgAdmin && {
            icon: 'mdi-math-log',
            id: 'organizationActivity',
            url: '/settings/organization-activity',
            title: this.$t('menu.admin.organization_logs'),
            action: () =>
              this.handleNavigation({
                id: 'organizationActivity',
                url: '/settings/organization-activity',
              }),
          },
      ])
    },
  },

  methods: {
    accessAllowed(acl) {
      return access.aclsAllowedByPlan(this.organization, acl)
    },

    handleNavigation(option) {
      this.trackMenuNavigate({ option: option.id })
      this.navigateTo(option.url)
    },

    getSitesURL() {
      const currentSiteId = this.$route?.params?.siteId || this.siteId
      if (this.organization?.plan === 'essentials' || this.isSiteAdmin) {
        return `/organizations/${this.organizationId}/sites/${currentSiteId}/settings`
      }

      return '/sites'
    },
  },
}
</script>
